import iconLogo from '../assets/art/icon-logo.svg?raw'
import { bindPortraitContentViewBox, svgFromRaw } from '../assets/art/svg'
import { el } from '../dom'
import type { GameId, GameModule } from '../games/types'
import { COPY } from './copy'

const TILE_TINT: Record<GameId, string> = {
  peekaboo: 'game-tile--uoa',
  animals: 'game-tile--animals',
  colors: 'game-tile--shapes',
  'folk-songs': '',
  drums: '',
  puzzle: '',
}

export function createHome(opts: {
  games: GameModule[]
  onStart: () => void
  onOpenGame: (id: GameId) => void
}): {
  root: HTMLElement
  setUnlocked: (unlocked: boolean) => void
  setMuted: (muted: boolean) => void
} {
  const start = el(
    'button',
    {
      className: 'start-btn',
      text: COPY.start,
      testId: 'start-button',
      attrs: { type: 'button' },
    },
  )
  start.addEventListener('click', () => opts.onStart())

  const speaker = createSpeaker()

  const liveGames = opts.games.filter((game) => game.playable)
  const comingGames = opts.games.filter((game) => !game.playable)

  const tiles = liveGames.map((game) => {
    const tint = TILE_TINT[game.id]
    const icon = homeTileIcon(game.iconSrc)
    const tile = el(
      'button',
      {
        className: tint ? `game-tile ${tint}` : 'game-tile',
        testId: `game-tile-${game.id}`,
        attrs: {
          type: 'button',
          'data-game-id': game.id,
          'aria-label': game.titleVi,
        },
      },
      [
        el('span', { className: 'game-tile__art' }, [icon]),
        el('span', { className: 'game-tile__label', text: game.titleVi }),
      ],
    )
    bindPortraitIconHeight(tile, icon)
    tile.addEventListener('click', () => opts.onOpenGame(game.id))
    return tile
  })

  const liveRow = el('div', { className: 'home__live', testId: 'game-grid' }, tiles)

  const comingItems = comingGames.map((game) =>
    el(
      'div',
      {
        className: 'coming-item',
        testId: `coming-${game.id}`,
        attrs: { 'aria-hidden': 'true' },
      },
      [svgFromRaw(game.iconSrc, 'coming-item__icon')],
    ),
  )
  const comingRow = el(
    'div',
    { className: 'home__coming', testId: 'coming-row', attrs: { 'aria-hidden': 'true' } },
    comingItems,
  )

  const root = el('section', { className: 'home', testId: 'home' }, [
    el('div', { className: 'home__brand' }, [
      svgFromRaw(iconLogo, 'home__mark'),
      el('h1', { className: 'home__title', text: COPY.productName }),
      speaker.root,
    ]),
    start,
    liveRow,
    comingRow,
  ])

  const setUnlocked = (unlocked: boolean) => {
    start.hidden = unlocked
    speaker.root.hidden = !unlocked
    liveRow.hidden = !unlocked
    comingRow.hidden = !unlocked
    root.classList.toggle('is-unlocked', unlocked)
  }

  setUnlocked(false)
  return { root, setUnlocked, setMuted: speaker.setMuted }
}

function homeTileIcon(raw: string): SVGSVGElement {
  const icon = svgFromRaw(raw, 'game-tile__icon')
  bindPortraitContentViewBox(icon)
  return icon
}

function bindPortraitIconHeight(tile: HTMLElement, icon: SVGSVGElement): void {
  const mq = window.matchMedia('(orientation: portrait)')
  const apply = () => {
    if (!mq.matches) {
      icon.style.height = ''
      return
    }
    const target = tile.offsetHeight * 0.65
    const viewH = icon.viewBox.baseVal.height
    let drawnH: number
    try {
      drawnH = icon.getBBox().height
    } catch {
      drawnH = viewH
    }
    const scale = drawnH > 0 ? viewH / drawnH : 1
    icon.style.height = `${Math.round(target * scale)}px`
  }
  new ResizeObserver(apply).observe(tile)
  mq.addEventListener('change', apply)
  apply()
}

function createSpeaker(): { root: HTMLElement; setMuted: (muted: boolean) => void } {
  const root = el('span', {
    className: 'home__speaker',
    testId: 'sound-state',
    attrs: { 'aria-label': COPY.audioReady },
  })
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
  svg.setAttribute('viewBox', '0 0 24 24')
  svg.setAttribute('class', 'home__speaker-icon')
  svg.setAttribute('aria-hidden', 'true')
  const body = document.createElementNS('http://www.w3.org/2000/svg', 'path')
  body.setAttribute('d', 'M3 9h4l5-4v14l-5-4H3z')
  body.setAttribute('fill', 'currentColor')
  const waves = document.createElementNS('http://www.w3.org/2000/svg', 'path')
  waves.setAttribute('class', 'home__speaker-waves')
  waves.setAttribute('d', 'M16 9a4 4 0 010 6M19 7a7 7 0 010 10')
  waves.setAttribute('fill', 'none')
  waves.setAttribute('stroke', 'currentColor')
  waves.setAttribute('stroke-width', '2')
  waves.setAttribute('stroke-linecap', 'round')
  const mute = document.createElementNS('http://www.w3.org/2000/svg', 'path')
  mute.setAttribute('class', 'home__speaker-mute')
  mute.setAttribute('d', 'M16 9l5 6M21 9l-5 6')
  mute.setAttribute('fill', 'none')
  mute.setAttribute('stroke', 'currentColor')
  mute.setAttribute('stroke-width', '2')
  mute.setAttribute('stroke-linecap', 'round')
  svg.append(body, waves, mute)
  root.append(svg)

  const setMuted = (muted: boolean) => {
    root.classList.toggle('is-muted', muted)
  }
  setMuted(false)
  return { root, setMuted }
}
